import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import { useState } from "react";
import { Trans } from "react-i18next";
import { ClaimKind } from "../shared/CaseModel";

const CLAIM_KINDS: ClaimKind[] = ["thirdPartyLiability", "policyholderDispute"];

export const CaseTitleDialog = ({
  initialTitle,
  initialClaimKind,
  open,
  saveTitle,
  onClose,
}: {
  initialTitle: string;
  // the kind of claim, asked for where an insurer names the claim
  initialClaimKind?: ClaimKind;
  open: boolean;
  saveTitle: (newTitle: string, claimKind?: ClaimKind) => Promise<void>;
  onClose: () => void;
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [claimKind, setClaimKind] = useState(initialClaimKind);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const isTitleEmpty = title === undefined || title.trim().length === 0;

  const close = () => {
    // is there anything to save?
    if (isTitleEmpty) {
      onClose();
      return;
    }
    const toSave = title;
    setSaving(true);
    saveTitle(toSave, claimKind)
      .then(() => {
        setSaving(false);
        setError(undefined);
      })
      .catch((error) => {
        setSaving(false);
        console.warn("Failed to save title", error);
        setError(error);
      });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ userSelect: "none" }}
    >
      <DialogTitle id="alert-dialog-title">
        <Trans i18nKey="case.title.editDialog.title" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            mb: 1,
          }}
        >
          <Trans i18nKey="case.title.editDialog.description" />
        </DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          id="title"
          label={`${t("case.title.editDialog.tip.title")}`}
          type="text"
          fullWidth
          variant="outlined"
          value={title}
          // pass name update
          onChange={(e) => setTitle(e.target.value)}
          error={isTitleEmpty}
          disabled={saving}
          helperText={
            isTitleEmpty
              ? t("case.title.editDialog.helper.emptyTitle")
              : undefined
          }
        />
        {claimKind && (
          <TextField
            select
            margin="dense"
            id="claimKind"
            label={t("case.claimKind.label")}
            fullWidth
            value={claimKind}
            onChange={(e) => setClaimKind(e.target.value as ClaimKind)}
            disabled={saving}
          >
            {CLAIM_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {t(`case.claimKind.${kind}`)}
              </MenuItem>
            ))}
          </TextField>
        )}
        {error && (
          <Alert sx={{ mt: 2 }} severity="error">
            <AlertTitle>
              <Trans i18nKey="case.title.editDialog.error.save" />
            </AlertTitle>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={saving}>
          <Trans i18nKey="case.title.editDialog.cancel" />
        </Button>
        <Button
          variant="contained"
          endIcon={<SaveIcon />}
          disabled={isTitleEmpty || saving}
          loading={saving}
          onClick={close}
        >
          <Trans i18nKey="case.title.editDialog.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
